// MaturaTrigTrianglePFClosedTask v2.6
// • dane wejściowe: sin/cos/tan α
// • stwierdzenia tylko liczbowe (bez opisów)
// • pełne wyjaśnienia: Pitagoras + 6 funkcji
// • poprawiona kolejność argumentów w D1 i D2 (trig, given)
// • dynamiczny diagram

import React from "react";
import TriangleDiagram from "../../../../components/diagrams/TriangleDiagram";
import TruthInput from "../../../../components/inputs/TruthInput";
import { InlineMath, BlockMath } from "react-katex";

/* ========================================================================== */
/* 1. PITAGOREJSKIE TRÓJKI — wartości liczbowe                                */
/* ========================================================================== */
const TRIPLES = [
  [3, 4, 5],
  [5, 12, 13],
  [8, 15, 17],
  [7, 24, 25],
  [20, 21, 29],
];

/* ========================================================================== */
/* 2. UTILS                                                                    */
/* ========================================================================== */
function gcd(a, b) {
  while (b) [a, b] = [b, a % b];
  return Math.abs(a);
}

function makeFrac(n, d) {
  const g = gcd(n, d);
  return { n: n / g, d: d / g };
}

function latexFrac(fr) {
  return fr.d === 1 ? `${fr.n}` : `\\frac{${fr.n}}{${fr.d}}`;
}

/* ========================================================================== */
/* 3. OBLICZENIA FUNKCJI TRYGO                                                 */
/* ========================================================================== */
function computeTrig(a, b, c, angleAt) {
  const oppA = angleAt === "B" ? b : a; // przeciwległa do α
  const adjA = angleAt === "B" ? a : b; // przyległa do α

  return {
    // α
    sinA: makeFrac(oppA, c),
    cosA: makeFrac(adjA, c),
    tanA: makeFrac(oppA, adjA),

    // β
    sinB: makeFrac(adjA, c),
    cosB: makeFrac(oppA, c),
    tanB: makeFrac(adjA, oppA),
  };
}

/* ========================================================================== */
/* 4. GENERATE GIVEN VALUE                                                     */
/* ========================================================================== */
function generateGiven(trig, type) {
  if (type === "sin") {
    return { type, value: trig.sinA, latex: latexFrac(trig.sinA) };
  }
  if (type === "cos") {
    return { type, value: trig.cosA, latex: latexFrac(trig.cosA) };
  }
  return { type: "tan", value: trig.tanA, latex: latexFrac(trig.tanA) };
}

/* ========================================================================== */
/* 5. STWIERDZENIA P/F — wszystko liczbowe                                     */
/* ========================================================================== */

// helpery
const st_true = (latex) => ({ latex, truth: true });
const st_false = (latex) => ({ latex, truth: false });

/* --- GRUPA A — relacje α ↔ β --- */
function A1(trig) {
  return st_true(`\\sin\\alpha = ${latexFrac(trig.cosB)}`);
}
function A2(trig) {
  return st_true(`\\cos\\alpha = ${latexFrac(trig.sinB)}`);
}
function A3(trig) {
  return st_false(`\\sin\\alpha = ${latexFrac(trig.sinB)}`);
}
function A4(trig) {
  return st_false(`\\cos\\alpha = ${latexFrac(trig.cosB)}`);
}
function A5(trig) {
  return st_false(`\\tan\\alpha = ${latexFrac(trig.tanB)}`);
}

/* --- GRUPA B — wartości funkcji α --- */
function B1(trig) {
  return st_true(`\\sin\\alpha = ${latexFrac(trig.sinA)}`);
}
function B2(trig) {
  return st_false(`\\sin\\alpha = ${latexFrac(trig.cosA)}`);
}
function B3(trig) {
  return st_true(`\\cos\\alpha = ${latexFrac(trig.cosA)}`);
}
function B4(trig) {
  return st_false(`\\cos\\alpha = ${latexFrac(trig.sinA)}`);
}
function B5(trig) {
  return st_true(`\\tan\\alpha = ${latexFrac(trig.tanA)}`);
}
function B6(trig) {
  return st_false(`\\tan\\alpha = ${latexFrac(trig.tanB)}`);
}

/* --- GRUPA C — wartości funkcji β --- */
function C1(trig) {
  return st_true(`\\sin\\beta = ${latexFrac(trig.sinB)}`);
}
function C2(trig) {
  return st_false(`\\sin\\beta = ${latexFrac(trig.sinA)}`);
}
function C3(trig) {
  return st_true(`\\cos\\beta = ${latexFrac(trig.cosB)}`);
}
function C4(trig) {
  return st_false(`\\cos\\beta = ${latexFrac(trig.cosA)}`);
}
function C5(trig) {
  return st_true(`\\tan\\beta = ${latexFrac(trig.tanB)}`);
}
function C6(trig) {
  return st_false(`\\tan\\beta = ${latexFrac(trig.tanA)}`);
}

/* --- GRUPA D — oparte o dane wejściowe --- */
// ⛔ UWAGA: poprawiona kolejność argumentów D1 / D2 → (trig, given)

function D1(trig, given) {
  // prawdziwe: "\sin α = 4/5" itd.
  return st_true(`\\${given.type}\\alpha = ${given.latex}`);
}

function D2(trig, given) {
  // fałszywe — użyjemy złej wartości, np. cosB
  return st_false(`\\${given.type}\\alpha = ${latexFrac(trig.cosB)}`);
}

/* ========================================================================== */
/* 6. LISTA WSZYSTKICH STWIERDZEŃ                                              */
/* ========================================================================== */
const STATEMENTS = [
  A1, A2, A3, A4, A5,
  B1, B2, B3, B4, B5, B6,
  C1, C2, C3, C4, C5, C6,
  D1, D2,
];

// stwierdzenia, które traktujemy jako "o kącie α" (mogą wspominać β, ale główny bohater to α)
const ALPHA_STATEMENT_FUNCS = [
  A1, A2, A3, A4, A5, // relacje z β
  B1, B2, B3, B4, B5, B6, // funkcje α
  D1, D2, // oparte o dane wejściowe dla α
];

// stwierdzenia, które są o kącie β
const BETA_STATEMENT_FUNCS = [
  C1, C2, C3, C4, C5, C6, // funkcje β
];

function buildCandidates(fns, trig, given, promptLatex) {
  const all = fns.map((fn) => fn(trig, given));
  const seen = new Set();
  const out = [];

  // 1) Nie pytamy o to samo co w poleceniu (ta sama funkcja i ten sam kąt α)
  const forbiddenLeft = `\\${given.type}\\alpha`; // np. "\tan\alpha", "\sin\alpha", "\cos\alpha"

  for (const st of all) {
    if (st.latex === promptLatex) continue; // nie powtarzamy polecenia

    // A) kasujemy trywialne "to samo co w poleceniu", tylko w formie stwierdzenia
    if (getLeftSide(st.latex) === forbiddenLeft) continue;

    // B) kasujemy trywialne: ta sama funkcja co w poleceniu + ta sama wartość RHS,
    //    ale np. z kątem beta (np. given: cosα=7/25, a stwierdzenie: cosβ=7/25)
    if (
      getFnType(st.latex) === given.type &&
      getRightSide(st.latex) === given.latex
    ) {
      continue;
    }

    if (seen.has(st.latex)) continue;
    seen.add(st.latex);
    out.push(st);
  }

  return out;
}



function getLeftSide(latex) {
  const idx = latex.indexOf("=");
  if (idx === -1) return latex.trim();
  return latex.slice(0, idx).trim(); // np. "\\cos\\alpha"
}


function getRightSide(latex) {
  const idx = latex.indexOf("=");
  if (idx === -1) return "";
  return latex.slice(idx + 1).trim(); // np. "\frac{7}{25}"
}

function getFnType(latex) {
  // patrzymy na lewą stronę: "\cos\alpha", "\sin\beta", "\tan\alpha"
  const left = getLeftSide(latex);
  if (left.includes("\\sin")) return "sin";
  if (left.includes("\\cos")) return "cos";
  if (left.includes("\\tan")) return "tan";
  return "";
}


/* ========================================================================== */
/* 7. GENERATE TASK                                                            */
/* ========================================================================== */
/**
 * Wariant „oryginał” – matura podstawowa, czerwiec 2025, zadanie 18:
 *   trójkąt prostokątny, sin α = 4/7 (przypadek NIEwymierny: cos α = √33/7).
 *   1) cos α = 3/7  → F   (bo cos α = √33/7 ≠ 3/7)
 *   2) cos β = 4/7  → P   (bo β = 90° − α, więc cos β = sin α = 4/7)
 */
function createJune2025OriginalTask() {
  return {
    type: "MaturaTrigTrianglePFClosedTask",
    data: {
      // a, b – tylko do schematycznego rysunku (w treści bez etykiet)
      a: 4,
      b: 6,
      c: 7,
      angleAt: "C",
      given: { type: "sin", latex: "\\frac{4}{7}" },
      s1: { latex: "\\cos\\alpha = \\frac{3}{7}", truth: false },
      s2: { latex: "\\cos\\beta = \\frac{4}{7}", truth: true },
      // znacznik wyjaśnienia dla przypadku niewymiernego
      irrational: {
        opp: 4,
        hyp: 7,
        adjLatex: "\\sqrt{33}",
        cosAlphaLatex: "\\dfrac{\\sqrt{33}}{7}",
      },
    },
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_CZE",
      taskNumber: 18,
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (
    generationOptions.variant === "original" &&
    generationOptions.session === "2025_CZE"
  ) {
    return createJune2025OriginalTask();
  }

  const [a, b, c] = TRIPLES[Math.floor(Math.random() * TRIPLES.length)];
  const angleAt = Math.random() < 0.5 ? "B" : "C";

  const trig = computeTrig(a, b, c, angleAt);

  const givenType = ["sin", "cos", "tan"][Math.floor(Math.random() * 3)];
  const given = generateGiven(trig, givenType);

  // latex zdania z polecenia, którego nie chcemy powtarzać
  const promptLatex = `\\${given.type}\\alpha = ${given.latex}`;

  // kandydaci: osobno zdania o α i o β
  let alphaCandidates = buildCandidates(
    ALPHA_STATEMENT_FUNCS,
    trig,
    given,
    promptLatex
  );
  let betaCandidates = buildCandidates(
    BETA_STATEMENT_FUNCS,
    trig,
    given,
    promptLatex
  );

  // awaryjne zabezpieczenie (nie powinno się zdarzyć)
  if (alphaCandidates.length === 0) {
    alphaCandidates = buildCandidates(STATEMENTS, trig, given, promptLatex);
  }
  if (betaCandidates.length === 0) {
    betaCandidates = buildCandidates(STATEMENTS, trig, given, promptLatex);
  }

  // wybieramy jedno zdanie o α
  const sAlpha =
    alphaCandidates[Math.floor(Math.random() * alphaCandidates.length)];

  let betaPool = betaCandidates.filter((st) => st.truth !== sAlpha.truth);

  // Nie chcemy "symetrii": dwa stwierdzenia z takim samym RHS (np. oba = 7/25)
  const rhsAlpha = getRightSide(sAlpha.latex);
  betaPool = betaPool.filter((st) => getRightSide(st.latex) !== rhsAlpha);

  if (betaPool.length === 0) {
    // awaryjnie: wróć do puli bez warunku prawdziwości, ale nadal bez tego samego RHS
    betaPool = betaCandidates.filter((st) => getRightSide(st.latex) !== rhsAlpha);
  }
  if (betaPool.length === 0) betaPool = betaCandidates; // ostateczny fallback

  const sBeta = betaPool[Math.floor(Math.random() * betaPool.length)];


  // losowo ustawiamy kolejność — raz α jako 1., raz jako 2.
  let s1, s2;
  if (Math.random() < 0.5) {
    s1 = sAlpha;
    s2 = sBeta;
  } else {
    s1 = sBeta;
    s2 = sAlpha;
  }

  return {
    type: "MaturaTrigTrianglePFClosedTask",
    data: { a, b, c, angleAt, trig, given, s1, s2 },
  };
}



/* ========================================================================== */
/* 8. RENDER TASK                                                              */
/* ========================================================================== */
export function renderTask({ data }) {
  const { a, b, angleAt, given, s1, s2 } = data;

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p><strong>Zadanie maturalne – zamknięte.</strong></p>

      <p>
        Dany jest trójkąt prostokątny o kątach ostrych{" "}
        <InlineMath math="\alpha" /> oraz <InlineMath math="\beta" />.
      </p>

      <p>
        Wiadomo, że{" "}
        <InlineMath math={`\\${given.type}\\alpha = ${given.latex}`} />.
      </p>

      <TriangleDiagram
        a={a}
        b={b}
        angleAt={angleAt}
        showSideLabels={false}
        showBetaAngle={true}
      />

      <p>
        Oceń prawdziwość poniższych stwierdzeń. Wybierz <InlineMath math="P" />,
        jeśli stwierdzenie jest prawdziwe, albo <InlineMath math="F" />, jeśli jest fałszywe.
      </p>

      <p><InlineMath math="1." /> <InlineMath math={s1.latex} /></p>
      <p><InlineMath math="2." /> <InlineMath math={s2.latex} /></p>
    </div>
  );
}

/* ========================================================================== */
/* 9. RENDER INPUT                                                             */
/* ========================================================================== */
export function renderInput(_task, userAnswer, onChange, disabled) {
  const v = userAnswer || {};

  const setValue = (key, val) => {
    onChange?.({
      ...v,
      [key]: val,
    });
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 12,
        marginTop: 12,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ minWidth: 18 }}>1.</span>
        <TruthInput
          value={v.s1 || null}
          disabled={disabled}
          onChange={(x) => setValue("s1", x)}
        />
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ minWidth: 18 }}>2.</span>
        <TruthInput
          value={v.s2 || null}
          disabled={disabled}
          onChange={(x) => setValue("s2", x)}
        />
      </div>
    </div>
  );
}


/* ========================================================================== */
/* 10. CHECK ANSWER                                                            */
/* ========================================================================== */
export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer) return false;

  const ok1 = userAnswer.s1 === (data.s1.truth ? "P" : "F");
  const ok2 = userAnswer.s2 === (data.s2.truth ? "P" : "F");

  return ok1 && ok2;
}

/* ========================================================================== */
/* 11. RENDER USER ANSWER                                                      */
/* ========================================================================== */
export function renderUserAnswer(ans) {
  if (!ans) return <i>(brak odpowiedzi)</i>;

  return (
    <div style={{ textAlign: "left" }}>
      <p><InlineMath math="1." /> <InlineMath math={`\\text{${ans.s1 || "-"}}`} /></p>
      <p><InlineMath math="2." /> <InlineMath math={`\\text{${ans.s2 || "-"}}`} /></p>
    </div>
  );
}

/* ========================================================================== */
/* 12. RENDER ANSWER                                                           */
/* ========================================================================== */
export function renderAnswer({ data }) {
  return (
    <div style={{ textAlign: "left" }}>
      <p><InlineMath math="1." /> <InlineMath math={`\\text{${data.s1.truth ? "P" : "F"}}`} /></p>
      <p><InlineMath math="2." /> <InlineMath math={`\\text{${data.s2.truth ? "P" : "F"}}`} /></p>
    </div>
  );
}

/* ========================================================================== */
/* 13. RENDER EXPLANATION — pełne objaśnienie                                  */
/* ========================================================================== */
function renderIrrationalExplanation(data) {
  const { irrational, s1, s2 } = data;
  const { opp, hyp, adjLatex, cosAlphaLatex } = irrational;
  const hyp2 = hyp * hyp;
  const opp2 = opp * opp;

  return (
    <div className="space-y-4" style={{ textAlign: "left" }}>
      <p>
        Dane:{" "}
        <InlineMath math={`\\sin\\alpha = \\dfrac{${opp}}{${hyp}}`} />.
      </p>

      <p>
        <strong>Krok 1.</strong> Z definicji sinusa{" "}
        <InlineMath math={"\\sin\\alpha = \\dfrac{\\text{przyprostokątna naprzeciw }\\alpha}{\\text{przeciwprostokątna}}"} />{" "}
        możemy przyjąć w podobnym trójkącie: przyprostokątna naprzeciw <InlineMath math="\alpha" /> ma
        długość <InlineMath math={`${opp}`} />, a przeciwprostokątna{" "}
        <InlineMath math={`${hyp}`} />. Drugą przyprostokątną{" "}
        <InlineMath math="x" /> (przyległą do <InlineMath math="\alpha" />)
        wyznaczamy z twierdzenia Pitagorasa:
      </p>
      <BlockMath math={`x^2 = ${hyp}^2 - ${opp}^2 = ${hyp2} - ${opp2} = ${hyp2 - opp2}`} />
      <BlockMath math={`x = ${adjLatex}`} />

      <p>
        <strong>Krok 2.</strong> Obliczamy{" "}
        <InlineMath math="\cos\alpha" />:
      </p>
      <BlockMath
        math={`\\cos\\alpha = \\dfrac{\\text{przyległa}}{\\text{przeciwprostokątna}} = ${cosAlphaLatex}`}
      />
      <p>
        Ponieważ <InlineMath math={`${cosAlphaLatex} \\neq \\dfrac{3}{${hyp}}`} />,
        stwierdzenie <InlineMath math={s1.latex} /> jest{" "}
        <strong>fałszywe</strong> (<InlineMath math="F" />).
      </p>

      <p>
        <strong>Krok 3.</strong> Kąty ostre trójkąta prostokątnego spełniają{" "}
        <InlineMath math={"\\alpha + \\beta = 90^\\circ"} />, więc{" "}
        <InlineMath math={"\\beta = 90^\\circ - \\alpha"} />. Stąd:
      </p>
      <BlockMath
        math={`\\cos\\beta = \\cos(90^\\circ - \\alpha) = \\sin\\alpha = \\dfrac{${opp}}{${hyp}}`}
      />
      <p>
        Zatem stwierdzenie <InlineMath math={s2.latex} /> jest{" "}
        <strong>prawdziwe</strong> (<InlineMath math="P" />).
      </p>
    </div>
  );
}

export function renderExplanation({ data }) {
  if (data.irrational) {
    return renderIrrationalExplanation(data);
  }

  const { a, b, c, angleAt, trig, given, s1, s2 } = data;

  const oppA = angleAt === "B" ? b : a;
  const adjA = angleAt === "B" ? a : b;

  const opp2 = oppA * oppA;
  const adj2 = adjA * adjA;
  const c2 = c * c;

  return (
    <div className="space-y-4" style={{ textAlign: "left" }}>
      {/* Podana wartość */}
      <p>
        Podano wartość{" "}
        <InlineMath math={`\\${given.type}\\alpha = ${given.latex}`} />.
      </p>

      {/* KROK 1 */}
      <p>
        <strong>Krok 1.</strong> Korzystamy z definicji funkcji
        trygonometrycznej oraz z twierdzenia Pitagorasa, aby wyznaczyć
        brakujący bok.
      </p>
      <p>
        Podany stosunek nie wyznacza długości boków. Możemy przyjąć wygodne
        długości w podobnym trójkącie prostokątnym, ponieważ wartości funkcji
        trygonometrycznych kątów ostrych pozostają wtedy takie same.
      </p>

      {given.type === "sin" && (
        <>
          <BlockMath math={`\\sin\\alpha = \\dfrac{\\text{przeciwległa}}{\\text{przeciwprostokątna}} = \\dfrac{${oppA}}{${c}}`} />
          <p>
            Przyjmujemy przeciwległą przyprostokątną długości{" "}
            <InlineMath math={`${oppA}`} /> oraz przeciwprostokątną{" "}
            <InlineMath math={`${c}`} />. Drugą przyprostokątną oznaczmy przez{" "}
            <InlineMath math="x" />.
          </p>
          <BlockMath math={`${oppA}^2 + x^2 = ${c}^2`} />
          <BlockMath math={`x^2 = ${c2} - ${opp2} = ${c2 - opp2}`} />
          <BlockMath math={`x = ${adjA}`} />
        </>
      )}

      {given.type === "cos" && (
        <>
          <BlockMath math={`\\cos\\alpha = \\dfrac{\\text{przyległa}}{\\text{przeciwprostokątna}} = \\dfrac{${adjA}}{${c}}`} />
          <p>
            Przyjmujemy przyległą przyprostokątną długości{" "}
            <InlineMath math={`${adjA}`} /> oraz przeciwprostokątną{" "}
            <InlineMath math={`${c}`} />. Drugą przyprostokątną oznaczmy przez{" "}
            <InlineMath math="x" />.
          </p>
          <BlockMath math={`x^2 + ${adjA}^2 = ${c}^2`} />
          <BlockMath math={`x^2 = ${c2} - ${adj2} = ${c2 - adj2}`} />
          <BlockMath math={`x = ${oppA}`} />
        </>
      )}

      {given.type === "tan" && (
        <>
          <BlockMath math={`\\tan\\alpha = \\dfrac{\\text{przeciwległa}}{\\text{przyległa}} = \\dfrac{${oppA}}{${adjA}}`} />
          <p>
            Przyjmujemy obie przyprostokątne:{" "}
            <InlineMath math={`${oppA}`} /> (przeciwległa do{" "}
            <InlineMath math="\alpha" />) oraz{" "}
            <InlineMath math={`${adjA}`} /> (przyległa do{" "}
            <InlineMath math="\alpha" />). Obliczamy przeciwprostokątną{" "}
            <InlineMath math="c" /> z twierdzenia Pitagorasa.
          </p>
          <BlockMath math={`${oppA}^2 + ${adjA}^2 = c^2`} />
          <BlockMath math={`c^2 = ${opp2} + ${adj2} = ${opp2 + adj2}`} />
          <BlockMath math={`c = ${c}`} />
        </>
      )}

      {/* Rysunek z podpisanymi bokami */}
      <p>
        Na rysunku wpisujemy długości boków i z takiego trójkąta możemy
        odczytać wszystkie funkcje trygonometryczne.
      </p>

      <TriangleDiagram
        a={a}
        b={b}
        angleAt={angleAt}
        showSideLabels={true}
        showBetaAngle={true}
      />

      {/* KROK 2 */}
      <p>
        <strong>Krok 2.</strong> Odczytujemy wartości funkcji dla kątów{" "}
        <InlineMath math="\alpha" /> i <InlineMath math="\beta" />.
      </p>

      {/* jedna linia dla α */}
      <BlockMath
        math={`\\sin\\alpha = ${latexFrac(
          trig.sinA
        )},\\quad \\cos\\alpha = ${latexFrac(
          trig.cosA
        )},\\quad \\tan\\alpha = ${latexFrac(trig.tanA)}`}
      />

      {/* druga linia dla β */}
      <BlockMath
        math={`\\sin\\beta = ${latexFrac(
          trig.sinB
        )},\\quad \\cos\\beta = ${latexFrac(
          trig.cosB
        )},\\quad \\tan\\beta = ${latexFrac(trig.tanB)}`}
      />

      {/* KROK 3 */}
      <p>
        <strong>Krok 3.</strong> Na podstawie powyższych wartości sprawdzamy
        prawdziwość stwierdzeń.
      </p>

      <p>
        <InlineMath math="1." /> <InlineMath math={s1.latex} /> —{" "}
        {s1.truth ? "prawda" : "fałsz"}.
      </p>

      <p>
        <InlineMath math="2." /> <InlineMath math={s2.latex} /> —{" "}
        {s2.truth ? "prawda" : "fałsz"}.
      </p>
    </div>
  );
}



/* ========================================================================== */
/* 14. EXPORT                                                                  */
/* ========================================================================== */
export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderUserAnswer,
  renderExplanation,
};
